import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";
import StudentModals from "../../studentModals";
import StudentSidebar from "../../student-details/studentSidebar";
import StudentBreadcrumb from "../../student-details/studentBreadcrumb";
import { useGetStudentByIdQuery } from "../../api/studentApi";
import { useStudentInvoices } from "./hooks/useStudentInvoices";
import { useRazorpayPayment } from "./hooks/useRazorpayPayment";
import ReceiptModal from "./components/ReceiptModal";
import { Checkbox } from "antd";

const StudentFees = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: studentResponse,
    isLoading: studentLoading,
    error: studentError,
  } = useGetStudentByIdQuery({ id: id || "" });

  const {
    invoiceRows,
    isLoading: invoiceLoading,
    error: invoiceError,
    summary,
  } = useStudentInvoices(id || "");

  // console.log(invoiceRows, 'InvoiceRows')

  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptInvoice, setSelectedReceiptInvoice] = useState<any>(null);
  const [groupedReceiptInvoices, setGroupedReceiptInvoices] = useState<any[]>([]);

  const location = useLocation();

  // Helper: find all invoices sharing a receipt_group_id
  const findGroupedInvoices = (receiptGroupId: string | null | undefined) => {
    if (!receiptGroupId) return [];
    return invoiceRows.filter((row: any) => row.receipt_group_id === receiptGroupId);
  };

  // Helper: open receipt modal for an invoice (handles grouping)
  const openReceiptModal = (invoiceRow: any) => {
    const grouped = findGroupedInvoices(invoiceRow.receipt_group_id);
    if (grouped.length > 1) {
      setGroupedReceiptInvoices(grouped);
      setSelectedReceiptInvoice(grouped[0]);
    } else {
      setGroupedReceiptInvoices([]);
      setSelectedReceiptInvoice(invoiceRow);
    }
    setShowReceiptModal(true);
  };

  useEffect(() => {
    if (invoiceRows.length > 0 && (location.state?.showReceiptForInvoiceId || location.state?.showReceiptGroupId)) {
      const receiptGroupId = location.state?.showReceiptGroupId;
      if (receiptGroupId) {
        // Grouped receipt: find all invoices in this group
        const grouped = findGroupedInvoices(receiptGroupId);
        if (grouped.length > 0) {
          setGroupedReceiptInvoices(grouped);
          setSelectedReceiptInvoice(grouped[0]);
          setShowReceiptModal(true);
        }
      } else {
        // Single invoice receipt
        const invoiceRow = invoiceRows.find((row: any) => row.id === location.state.showReceiptForInvoiceId);
        if (invoiceRow) {
          openReceiptModal(invoiceRow);
        }
      }
      // Clear the state so it doesn't reopen on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, invoiceRows]);

  // console.log(invoiceRows, 'ir---')

  const { isCreating, isVerifying } = useRazorpayPayment();

  const handleSelectInvoice = (invoiceId: number, checked: boolean) => {
    let newSelected = [...selectedInvoices];

    if (checked) {
      if (!newSelected.includes(invoiceId)) newSelected.push(invoiceId);
    } else {
      newSelected = newSelected.filter((id) => id !== invoiceId);
    }

    setSelectedInvoices(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allSelectable = invoiceRows
        .filter((row: any) => row.status !== "completed")
        .map((row: any) => row.id);
      setSelectedInvoices(allSelectable);
    } else {
      setSelectedInvoices([]);
    }
  };

  if (studentLoading || invoiceLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <StudentBreadcrumb studentId={id} />
          </div>
          <div className="row">
            <StudentSidebar />
            <div className="col-xxl-9 col-xl-8">
              <div className="row">
                <div className="col-md-12">
                  {/* Navigation Tabs */}
                  <ul className="nav nav-tabs nav-tabs-bottom mb-4">
                    <li>
                      <Link
                        to={`${routes.studentDetail.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-school me-2" />
                        Student Details
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentAttendanceDetail.replace(
                          ":id",
                          id || ""
                        )}`}
                        className="nav-link"
                      >
                        <i className="ti ti-calendar-due me-2" />
                        Attendance
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentFees.replace(":id", id || "")}`}
                        className="nav-link active"
                      >
                        <i className="ti ti-report-money me-2" />
                        Fees
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentResult.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-bookmark-edit me-2" />
                        Results
                      </Link>
                    </li>
                  </ul>

                  <div className="card">
                    <div className="card-body p-0 py-3">
                      <div className="text-center">
                        Loading student fee data...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (studentError || invoiceError) {
    return (
      <>
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <StudentBreadcrumb studentId={id} />
            </div>
            <div className="row">
              <StudentSidebar />
              <div className="col-xxl-9 col-xl-8">
                <div className="row">
                  <div className="col-md-12">
                    {/* Navigation Tabs */}
                    <ul className="nav nav-tabs nav-tabs-bottom mb-4">
                      <li>
                        <Link
                          to={`${routes.studentDetail.replace(
                            ":id",
                            id || ""
                          )}`}
                          className="nav-link"
                        >
                          <i className="ti ti-school me-2" />
                          Student Details
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${routes.studentAttendanceDetail.replace(
                            ":id",
                            id || ""
                          )}`}
                          className="nav-link"
                        >
                          <i className="ti ti-calendar-due me-2" />
                          Attendance
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${routes.studentFees.replace(":id", id || "")}`}
                          className="nav-link active"
                        >
                          <i className="ti ti-report-money me-2" />
                          Fees
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${routes.studentResult.replace(
                            ":id",
                            id || ""
                          )}`}
                          className="nav-link"
                        >
                          <i className="ti ti-bookmark-edit me-2" />
                          Results
                        </Link>
                      </li>
                    </ul>

                    {/* Show the error message centered */}
                    <div
                      className="card"
                      style={{ height: "calc(100vh - 300px)" }}
                    >
                      <div className="card-body d-flex align-items-center justify-content-center">
                        <div className="text-center">
                          <div className="mb-3">
                            <i className="ti ti-file-invoice fa-3x text-primary"></i>
                          </div>
                          <h4 className="mb-2">Invoices Not Generated</h4>
                          <p className="text-muted mb-4">
                            It looks like invoices have not been generated for
                            this student yet.
                          </p>
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              navigate(routes.generateInvoices);
                            }}
                          >
                            <i className="ti ti-plus me-2"></i>
                            Generate Invoices
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <StudentModals />
      </>
    );
  }

  // Navigate to invoice when stage is clicked
  const handleStageClick = (invoiceId: number) => {
    navigate(
      routes.studentFeesInvoice
        .replace(":id", id || "")
        .replace(":invoiceId", invoiceId.toString())
    );
  };

  const renderActionDropdown = (
    invoiceRow: any,
    isCreating: boolean,
    isVerifying: boolean
  ) => {
    const isPending = invoiceRow.status !== "completed";
    const invoiceId = invoiceRow.id;
    const partial = invoiceRow.status === "partial";
    const isStudentActive = student?.status === "active" || student?.status === "pending";

    return (
      <div className="dropdown">
        <button className="btn btn-light btn-sm" data-bs-toggle="dropdown">
          <i className="ti ti-dots-vertical"></i>
        </button>
        <ul className="dropdown-menu">
          <li>
            <Link
              className="dropdown-item"
              to={`${routes.studentFeesInvoice
                .replace(":id", id || "")
                .replace(":invoiceId", invoiceId.toString())}`}
            >
              <i className="ti ti-file-invoice me-2"></i>
              View Invoice
            </Link>
          </li>

          {(invoiceRow.status === "completed" || invoiceRow.status === "partial" || Number(invoiceRow.paidAmount || 0) > 0) ? (
            <li>
              <Link
                className="dropdown-item"
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  openReceiptModal(invoiceRow);
                }}
              >
                <i className="ti ti-receipt me-2"></i>
                Receipt
              </Link>
            </li>
          ) : null}

          {isStudentActive && isPending && (
            <>
              {!partial && (
                <li>
                  <Link
                    className="dropdown-item"
                    to={`${routes.studentFeesDiscount
                      .replace(":id", id || "")
                      .replace(":invoiceId", invoiceId.toString())}`}
                  >
                    <i className="ti ti-discount-2 me-2"></i>
                    Apply Discount
                  </Link>
                </li>
              )}
            </>
          )}
        </ul>
      </div>
    );
  };

  // Extract student info
  const student = studentResponse?.data?.student;
  const courseName =
    invoiceRows.length > 0 ? invoiceRows[0].courseName : "Not Available";

  const handleCollectSelectedFees = async () => {
    if (
      !student?.id ||
      !student?.center.id ||
      selectedInvoices.length === 0 ||
      (student?.status !== "active" && student?.status !== "pending")
    )
      return;

    const selectedRows = invoiceRows.filter((row) =>
      selectedInvoices.includes(row.id)
    );

    // Calculate total pending amount for all selected invoices
    const totalAmount = selectedRows.reduce((sum, row) => {
      return sum + Number(row.payable || 0);
    }, 0);

    const invoiceNumbers = selectedRows.map(row => row.invoice_number).join(', ');

    // Navigate to PaymentDetails with necessary props
    navigate(routes.paymentFeeDetails, {
      state: {
        student,
        invoices: selectedInvoices,
        invoiceNumbers: invoiceNumbers,
        totalAmount,
      },
    });
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <StudentBreadcrumb studentId={id} />
          </div>
          <div className="row">
            <StudentSidebar />
            <div className="col-xxl-9 col-xl-8">
              <div className="row">
                <div className="col-md-12">
                  {/* Navigation Tabs */}
                  <ul className="nav nav-tabs nav-tabs-bottom mb-4">
                    <li>
                      <Link
                        to={`${routes.studentDetail.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-school me-2" />
                        Student Details
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentAttendanceDetail.replace(
                          ":id",
                          id || ""
                        )}`}
                        className="nav-link"
                      >
                        <i className="ti ti-calendar-due me-2" />
                        Attendance
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentFees.replace(":id", id || "")}`}
                        className="nav-link active"
                      >
                        <i className="ti ti-report-money me-2" />
                        Fees
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentResult.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-bookmark-edit me-2" />
                        Results
                      </Link>
                    </li>
                  </ul>

                  {/* Course Fee Details Card */}
                  <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between">
                      <h4 className="mb-0">Course Fee Details</h4>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() =>
                            navigate(
                              `${routes.billingSection.replace(
                                ":id",
                                id || ""
                              )}`
                            )
                          }
                        >
                          <i className="ti ti-calculator me-2"></i>
                          Billing Overview
                        </button>

                          <button
                            className="btn btn-sm btn-primary"
                            onClick={handleCollectSelectedFees}
                            disabled={
                              selectedInvoices.length === 0 ||
                              isCreating ||
                              isVerifying ||
                              (student?.status !== "active" && student?.status !== "pending")
                            }
                          >
                            <i className="ti ti-cash me-2"></i>
                            {isCreating || isVerifying
                              ? "Processing..."
                              : "Collect Selected Fees"}
                          </button>
                      </div>
                    </div>
                    <div className="card-body">
                      {/* Course Summary */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <div className="d-flex align-items-center mb-3">
                            <div className="flex-shrink-0">
                              <span className="avatar avatar-md bg-light-primary rounded">
                                <i className="ti ti-book fs-18"></i>
                              </span>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h6 className="mb-0">Course</h6>
                              <small className="text-muted">{courseName}</small>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex align-items-center mb-3">
                            <div className="flex-shrink-0">
                              <span className="avatar avatar-md bg-light-success rounded">
                                <i className="ti ti-cash fs-18"></i>
                              </span>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h6 className="mb-0">Total Fee</h6>
                              <small className="text-muted">
                                ₹{summary.totalAmount.toLocaleString()}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fee Levels Table */}
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead className="thead-light">
                            <tr>
                              <th>
                                <Checkbox
                                  checked={
                                    invoiceRows.filter((r: any) => r.status !== "completed").length > 0 &&
                                    selectedInvoices.length === invoiceRows.filter((r: any) => r.status !== "completed").length
                                  }
                                  onChange={(e) => handleSelectAll(e.target.checked)}
                                  aria-label="Select all"
                                />
                              </th>
                              <th>Level</th>
                              <th>Due Date</th>
                              <th>Fee Type</th>
                              <th>Amount</th>
                              <th>Discount</th>
                              <th>Payable</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoiceRows.length > 0 ? (
                              invoiceRows.map((row) => {
                                return (
                                  <tr key={row.id}>
                                    <td>
                                      <Checkbox
                                        checked={selectedInvoices.includes(row.id)}
                                        onChange={(e) => handleSelectInvoice(row.id, e.target.checked)}
                                        disabled={row.status === "completed"}
                                      />
                                    </td>
                                    <td
                                      onClick={() => handleStageClick(row.id)}
                                      style={{ cursor: "pointer" }}
                                      className="text-primary"
                                    >
                                      <div className="d-flex align-items-center">
                                        <span>
                                          {row.type === "registrationfee"
                                            ? "Registration"
                                            : row.stageName}
                                        </span>
                                      </div>
                                    </td>
                                    <td>{row.dueDate}</td>
                                    <td>
                                      <span
                                        className={`badge badge-soft-${row.type === "registrationfee"
                                          ? "primary"
                                          : "success"
                                          }`}
                                      >
                                        {row.type === "registrationfee"
                                          ? "Registration Fee"
                                          : "Course Fee"}
                                      </span>
                                    </td>
                                    <td>₹{row.amount.toLocaleString()}</td>
                                    <td>₹{row.discount.toLocaleString()}</td>
                                    <td>
                                      <div>₹{row.payable.toLocaleString()}</div>
                                      {row.status === "partial" && Number(row.paidAmount || 0) > 0 ? (
                                        <small className="text-muted d-block">
                                          Paid: ₹{row.paidAmount.toLocaleString()}
                                        </small>
                                      ) : null}
                                    </td>
                                    <td>
                                      <span
                                        className={`badge badge-soft-${row.status === "completed"
                                          ? "success"
                                          : row.status === "partial"
                                            ? "info"
                                            : "warning"
                                          }`}
                                      >
                                        <i className="ti ti-circle-filled fs-5 me-1"></i>
                                        {row.status === "completed"
                                          ? "Paid"
                                          : row.status === "partial"
                                            ? "Partial"
                                            : "Pending"}
                                      </span>
                                    </td>
                                    <td>
                                      {renderActionDropdown(
                                        row,
                                        isCreating,
                                        isVerifying
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr style={{ height: "300px" }}>
                                <td colSpan={9} className="align-middle">
                                  <div className="d-flex align-items-center justify-content-center h-100">
                                    <div className="text-center">
                                      <div className="mb-3">
                                        <i className="ti ti-file-invoice fa-3x text-primary"></i>
                                      </div>
                                      <h4 className="mb-2">
                                        Invoices Not Generated
                                      </h4>
                                      <p className="text-muted mb-4">
                                        It looks like invoices have not been
                                        generated for this student yet.
                                      </p>
                                      <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                          navigate(routes.generateInvoices);
                                        }}
                                      >
                                        <i className="ti ti-square-rounded-plus me-2"></i>
                                        Generate Invoices
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}

                            {/* Summary Row */}
                            {invoiceRows.length > 0 && (
                              <tr className="table-dark">
                                <td className="bg-dark text-white" colSpan={4}>
                                  <strong>Total</strong>
                                </td>
                                <td className="bg-dark text-white">
                                  <strong>
                                    ₹{summary.totalAmount.toLocaleString()}
                                  </strong>
                                </td>
                                <td className="bg-dark text-white">
                                  <strong>
                                    ₹{summary.totalDiscount.toLocaleString()}
                                  </strong>
                                </td>
                                <td className="bg-dark text-white">
                                  <strong>
                                    ₹{summary.totalPayable.toLocaleString()}
                                  </strong>
                                </td>
                                <td
                                  className="bg-dark text-white"
                                  colSpan={2}
                                ></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StudentModals />

      {showReceiptModal && (
        <ReceiptModal
          invoiceData={selectedReceiptInvoice}
          groupedInvoices={groupedReceiptInvoices.length > 1 ? groupedReceiptInvoices : undefined}
          studentData={student}
          onClose={() => {
            setShowReceiptModal(false);
            setGroupedReceiptInvoices([]);
          }}
        />
      )}
    </>
  );
};

export default StudentFees;